import { type AnyObject } from '@Types';
import {
    createOutsideProjectFieldsModal,
    type IguanaCohortInput,
    type IguanaLearnerProjectInput,
    type IguanaProjectProgressInput,
} from 'ProjectFieldsModal';
import transformKeyCase from 'Utils/transformKeyCase';
import { invalidateAllProjectSubmissionTags, type ProjectSubmission } from '../../api';
import {
    type EvaluationModalDataResult,
    type IguanaModel,
    type OpenEvaluationModalCoreParams,
} from './evaluationModal.types';

/**
 * Transforms fetch result data to snake_case format for Iguana models.
 * Creates a default project progress object if none exists in the fetch result.
 */
function transformFetchResultData(
    fetchResult: EvaluationModalDataResult,
    submission: ProjectSubmission,
): {
    learnerProjectData: AnyObject;
    cohortData: AnyObject;
    projectProgressData: AnyObject;
    userData: AnyObject;
    projectGroupMembershipData: AnyObject | null;
} {
    const learnerProjectData = transformKeyCase<AnyObject>(fetchResult.learnerProject, { to: 'snakeCase' });
    const cohortData = transformKeyCase<AnyObject>(fetchResult.cohort, { to: 'snakeCase' });
    const userData = transformKeyCase<AnyObject>(fetchResult.user, { to: 'snakeCase' });
    const projectGroupMembershipData = fetchResult.projectGroupMembership
        ? transformKeyCase<AnyObject>(fetchResult.projectGroupMembership, { to: 'snakeCase' })
        : null;
    const projectProgressData = fetchResult.projectProgress
        ? transformKeyCase<AnyObject>(fetchResult.projectProgress, { to: 'snakeCase' })
        : {
              requirement_identifier: submission.requirementIdentifier,
              status: null,
              id_verified: null,
              historical_project_submissions: [],
          };

    return { learnerProjectData, cohortData, projectProgressData, userData, projectGroupMembershipData };
}

/**
 * Opens the evaluation modal for a project submission.
 * Handles data transformation and cache invalidation.
 */
export async function openEvaluationModalCore({
    submission,
    $injector,
    dispatch,
    fetchResult,
    onSuccess,
    onClose,
}: OpenEvaluationModalCoreParams): Promise<void> {
    const LearnerProject = $injector.get<IguanaModel>('LearnerProject');
    const ProjectProgress = $injector.get<IguanaModel>('ProjectProgress');
    const Cohort = $injector.get<IguanaModel>('Cohort');

    const { learnerProjectData, cohortData, projectProgressData, userData, projectGroupMembershipData } =
        transformFetchResultData(fetchResult, submission);

    // Build Iguana models so the shared adapter can use computed getters (e.g. getPassingScore).
    const project = LearnerProject.new(learnerProjectData);
    const cohort = Cohort.new(cohortData);
    const projectProgress = ProjectProgress.new(projectProgressData);

    createOutsideProjectFieldsModal({
        project: project as unknown as IguanaLearnerProjectInput,
        projectProgress: projectProgress as unknown as IguanaProjectProgressInput,
        cohort: cohort as unknown as IguanaCohortInput,
        user: { id: userData.id as string, name: userData.name as string, email: userData.email as string },
        projectGroupMembership: projectGroupMembershipData,
        onSaved: () => {
            dispatch(invalidateAllProjectSubmissionTags(submission.id));
            onSuccess?.();
        },
        onClose,
    });
}
